// ===========================================
// Not proceeding (#374 release B, plan P4)
// ===========================================
// ForfeitRegistrationFeeDialog: the office marks a HELD fee Not proceeding. The child is not coming,
// the school keeps the fee, and it is never credited on an invoice. The reason is the school's own
// closing reason (the enquiry list), and "Other" needs a detail. When it was the enquiry's last
// child, the server moves the enquiry too; the dialog says what happened to it.
//
// ReverseForfeitDialog: the owner undoes it (reason required). It never reopens the enquiry.
// Failures show inline and switch on the error code, never the text.

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import type { ForfeitEnquiryOutcome, RegistrationFeeView } from '../../../api/familyPayments.api';
import {
  useClearRegistrationFeeRefundFlag,
  useCloseRegistrationFeeAsRefunded,
  useForfeitReasons,
  useForfeitRegistrationFee,
  useReverseRegistrationFeeForfeit,
} from '../../../hooks/useFamilyPayments';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '../../../utils/apiError';

/** What the snackbar says about the enquiry, or null when there is nothing to say. */
export function enquiryOutcomeText(outcome: ForfeitEnquiryOutcome): string | null {
  if (outcome.moved) return 'The enquiry moved to Not proceeding.';
  switch (outcome.why) {
    case 'OTHER_CHILDREN':
      return 'The enquiry stays open: another child is still in it.';
    case 'NO_PERMISSION':
      return 'The enquiry was not changed: you cannot move enquiries.';
    default:
      return null;
  }
}

export function forfeitErrorText(error: unknown): string {
  switch (apiErrorCode(error)) {
    case 'REGISTRATION_FEE_CREDITED': {
      const invoiceNumber = apiErrorDetails<{ invoiceNumber?: string }>(error)?.invoiceNumber;
      return `This fee is credited on ${invoiceNumber ? `invoice ${invoiceNumber}` : 'an invoice'}, so the child went ahead.`;
    }
    case 'REGISTRATION_FEE_FORFEITED':
      return 'This fee is already marked Not proceeding.';
    case 'REGISTRATION_FEE_UNASSIGNED':
      return 'Assign this fee to a child first.';
    case 'REGISTRATION_FEE_COVERS_FAMILY':
      return 'This fee covers the whole family, and another child is still active. If it is on the wrong child, use Assign to child instead.';
    case 'REGISTRATION_FEE_NOT_HELD':
      return 'The school keeps this fee anyway, so there is nothing to mark.';
    case 'REGISTRATION_FEE_NEWER_FEE':
      return 'A newer registration fee covers this child now, so this one cannot be made live again.';
    case 'REGISTRATION_FEE_NOT_REFUND_FLAGGED':
      return 'This fee has no refund flag to clear.';
    case 'REGISTRATION_FEE_WAIVED':
      return 'The registration fee is waived for this family. Revoke the waiver first.';
    default:
      return apiErrorMessage(error, 'Could not save');
  }
}

interface ForfeitProps {
  open: boolean;
  onClose: () => void;
  familyId: string | undefined;
  /** The fee (Payment History's row, or the enquiry panel's child: only its id and child are read). */
  fee: (Pick<RegistrationFeeView, 'id' | 'student'> & { state?: RegistrationFeeView['state'] }) | null;
}

export function ForfeitRegistrationFeeDialog({ open, onClose, familyId, fee }: ForfeitProps) {
  const { enqueueSnackbar } = useSnackbar();
  const reasons = useForfeitReasons(open);
  const mutation = useForfeitRegistrationFee();
  const [reasonId, setReasonId] = useState('');
  const [detail, setDetail] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReasonId('');
    setDetail('');
    setError(null);
  }, [open, fee?.id]);

  const reason = reasons.data?.find((r) => r.id === reasonId);
  const needsDetail = !!reason?.needsDetail;
  const valid = !!reasonId && (!needsDetail || detail.trim().length > 0);
  const childName = fee?.student?.name ?? 'the child';

  const handleClose = () => {
    if (!mutation.isPending) onClose();
  };

  const handleSubmit = async () => {
    if (!familyId || !fee || !valid) return;
    setError(null);
    try {
      const result = await mutation.mutateAsync({ familyId, id: fee.id, reasonId, detail: detail.trim() || undefined });
      const about = enquiryOutcomeText(result.enquiry);
      enqueueSnackbar(`${childName}: Not proceeding.${about ? ` ${about}` : ''}`, { variant: 'success' });
      onClose();
    } catch (e) {
      setError(forfeitErrorText(e));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Not proceeding</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {fee?.state === 'REFUND_FLAGGED'
              ? 'Stripe refunded this fee. Not proceeding closes it: it will not be credited.'
              : `${childName} is not coming. The school keeps the registration fee, and it will not come off an invoice.`}{' '}
            If this was the enquiry's last child, the enquiry moves to Not proceeding too.
          </Typography>
          {reasons.isError && <Alert severity="error">Could not load the reasons. Close this and try again.</Alert>}
          <FormControl fullWidth size="small" disabled={mutation.isPending || reasons.isLoading} required>
            <InputLabel id="forfeit-reason-label">Reason</InputLabel>
            <Select
              labelId="forfeit-reason-label"
              label="Reason"
              value={reasonId}
              onChange={(e) => setReasonId(e.target.value as string)}
            >
              {(reasons.data ?? []).map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label={needsDetail ? 'Detail (required)' : 'Detail (optional)'}
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            multiline
            rows={2}
            disabled={mutation.isPending}
            inputProps={{ maxLength: 500 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending} sx={{ minHeight: { xs: 44 } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minHeight: { xs: 44 } }}
        >
          {mutation.isPending ? 'Saving...' : 'Not proceeding'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ReverseForfeitDialog({ open, onClose, familyId, fee }: ForfeitProps) {
  const mutation = useReverseRegistrationFeeForfeit();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError(null);
  }, [open, fee?.id]);

  const trimmed = reason.trim();
  const handleClose = () => {
    if (!mutation.isPending) onClose();
  };
  const handleSubmit = async () => {
    if (!familyId || !fee || !trimmed) return;
    setError(null);
    try {
      await mutation.mutateAsync({ familyId, id: fee.id, reason: trimmed });
      onClose();
    } catch (e) {
      setError(forfeitErrorText(e));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Undo Not proceeding</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            The fee goes back to Held, and comes off {fee?.student?.name ?? 'the child'}'s first invoice. The enquiry is not
            reopened: move it yourself if you need to.
          </Typography>
          <TextField
            fullWidth
            required
            autoFocus
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={2}
            disabled={mutation.isPending}
            inputProps={{ maxLength: 500 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending} sx={{ minHeight: { xs: 44 } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!trimmed || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minHeight: { xs: 44 } }}
        >
          {mutation.isPending ? 'Saving...' : 'Undo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** #374 P7: the owner clears a Stripe refund flag (note required); the fee goes back to Held. */
export function ClearRefundFlagDialog({ open, onClose, familyId, fee }: ForfeitProps) {
  const mutation = useClearRegistrationFeeRefundFlag();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNote('');
    setError(null);
  }, [open, fee?.id]);

  const trimmed = note.trim();
  const handleClose = () => {
    if (!mutation.isPending) onClose();
  };
  const handleSubmit = async () => {
    if (!familyId || !fee || !trimmed) return;
    setError(null);
    try {
      await mutation.mutateAsync({ familyId, id: fee.id, note: trimmed });
      onClose();
    } catch (e) {
      setError(
        apiErrorCode(e) === 'REGISTRATION_FEE_FORFEITED'
          ? 'This fee is marked Not proceeding, so its refund flag stays.'
          : forfeitErrorText(e)
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Clear refund flag</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Clear the flag only if the fee still stands (for example, only the card fee was refunded). The fee goes back to
            how it was before the refund. A larger refund later flags it again.
          </Typography>
          <TextField
            fullWidth
            required
            autoFocus
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            rows={2}
            disabled={mutation.isPending}
            inputProps={{ maxLength: 500 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending} sx={{ minHeight: { xs: 44 } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!trimmed || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minHeight: { xs: 44 } }}
        >
          {mutation.isPending ? 'Saving...' : 'Clear flag'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function closeRefundedErrorText(error: unknown): string {
  switch (apiErrorCode(error)) {
    case 'REGISTRATION_FEE_CREDITED': {
      const invoiceNumber = apiErrorDetails<{ invoiceNumber?: string }>(error)?.invoiceNumber;
      return `This fee is credited on ${invoiceNumber ? `invoice ${invoiceNumber}` : 'an invoice'}. Take the credit off that invoice first.`;
    }
    case 'REGISTRATION_FEE_FORFEITED':
      return 'This fee is marked Not proceeding, so it cannot be closed as refunded.';
    case 'REGISTRATION_FEE_NOT_REFUND_FLAGGED':
      return 'This fee has no active refund flag to close.';
    case 'REGISTRATION_FEE_CHANGED':
      return 'This fee changed while you were looking at it. Close this and try again.';
    default:
      return apiErrorMessage(error, 'Could not save');
  }
}

/** The owner closes a Stripe-refunded fee as Refunded (note required): the child is going ahead. */
export function CloseAsRefundedDialog({ open, onClose, familyId, fee }: ForfeitProps) {
  const mutation = useCloseRegistrationFeeAsRefunded();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNote('');
    setError(null);
  }, [open, fee?.id]);

  const trimmed = note.trim();
  const handleClose = () => {
    if (!mutation.isPending) onClose();
  };
  const handleSubmit = async () => {
    if (!familyId || !fee || !trimmed) return;
    setError(null);
    try {
      await mutation.mutateAsync({ familyId, id: fee.id, note: trimmed });
      onClose();
    } catch (e) {
      setError(closeRefundedErrorText(e));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Close as refunded</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Use this when Stripe refunded the fee but the child is going ahead (for example, the family paid another way).
            The fee is marked Refunded and is never credited; you can then record the fee they pay instead. The enquiry is
            not changed.
          </Typography>
          <TextField
            fullWidth
            required
            autoFocus
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            rows={2}
            disabled={mutation.isPending}
            inputProps={{ maxLength: 500 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending} sx={{ minHeight: { xs: 44 } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!trimmed || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minHeight: { xs: 44 } }}
        >
          {mutation.isPending ? 'Saving...' : 'Close as refunded'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
